import {compact} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSpec,
  ProcessFlowchartConfigSpec,
  ProcessFlowchartEdgeConfigSpec,
  ProcessFlowchartNodeConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
  ProcessFlowchartSpec,
  TaskDefinitionVersionSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {convertToRouterConfigSpec} from 'processes/modals/router-config/RouterConfigUtils';
import {
  StaticTemplateFlowchart,
  ProcessFlowchartEdgeType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {isStartOrEndNode} from './adapterUtils';
import buildSchemaMapperConfigsFromStaticData from './buildSchemaMapperConfigFromStaticData';
import {convertFrontendTaskDefinitionVersionSpecToNetworkSpec} from './processFlowchartConfigEditorNetworkUtils';
import {
  isCollapsedStaticNodeData,
  isStaticEndNodeData,
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  RouterStaticNodeData,
  EndStaticNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
  isStaticPlaceholderNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartConfigMetadata} from './redux/reduceFlowchartConfig';
import {ConfigurableTaskDefinitionGQL} from './useAllSchemasById';

export interface BuildNetworkFlowchartFromStaticDataRunnerOptions extends Omit<
  FlowchartConfigMetadata,
  'interactionState' | 'currentVersionId' | 'didInitialFlowchartHaveChainedRouters' | 'flowchartTypeName'
> {
  flowchartConfigId: string;
  /**
   * Maps PTDV client IDs to task schema IDs.
   * Used to include optional-transition-only task schemas in availableTaskSchemaIds
   * when they have no corresponding node on the main canvas.
   */
  ptdvIdToTaskSchemaId: Record<string, string>;
}

interface BuildNetworkFlowchartFromStaticDataRunnerArgs {
  staticFlowchart: StaticTemplateFlowchart;
  options: BuildNetworkFlowchartFromStaticDataRunnerOptions;
}

class BuildNetworkFlowchartFromStaticDataRunner {
  private readonly staticFlowchart: StaticTemplateFlowchart;
  private readonly options: BuildNetworkFlowchartFromStaticDataRunnerOptions;
  private inboundSchemaMapperConfigsByNodeId!: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
  private rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;

  constructor(args: BuildNetworkFlowchartFromStaticDataRunnerArgs) {
    this.staticFlowchart = args.staticFlowchart;
    this.options = args.options;

    assert(this.options.rootTaskSchema != null, 'Expected root task schema to be defined');
    this.rootTaskSchema = this.options.rootTaskSchema;
  }

  run(): ProcessFlowchartConfigSpec {
    return this.convertStaticFlowchartToNetworkFlowchartConfigSpec();
  }

  /**
   * Converts a static flowchart into a network spec ProcessFlowchartConfigSpec used for updates.
   */
  private convertStaticFlowchartToNetworkFlowchartConfigSpec(): ProcessFlowchartConfigSpec {
    const {
      // TODO: This is just for schema mapper configs in the flowchart, doesn't account for those outside of the flowchart.
      // To be solved once ACM/PCM decision made.
      schemaMapperConfigSpecs: updatedSchemaMapperConfigSpecs,
      // Inbound schema mappers are only those that are used in the flowchart.
      inboundSchemaMapperConfigsByNodeId,
    } = buildSchemaMapperConfigsFromStaticData({
      nodes: this.staticFlowchart.nodesStaticData,
      edges: this.staticFlowchart.edgesStaticData,
      transitions: this.options.transitions,
    });

    this.inboundSchemaMapperConfigsByNodeId = inboundSchemaMapperConfigsByNodeId;

    const {rootTaskDefinitionVersionSpec, allTaskDefinitionVersionSpecs} =
      this.extractTaskDefinitionVersionSpecs();

    const availableTaskSchemaIdSet = new Set(
      compact(
        this.staticFlowchart.nodesStaticData
          .filter((node) => isStaticTaskSchemaNodeData(node) && !isStartOrEndNode(node.nodeDetailsType))
          .map((node: TaskSchemaStaticNodeData) => node.taskSchemaId)
      )
    );

    for (const transition of Object.values(this.options.transitions)) {
      if (transition.isPlanned) {
        continue;
      }
      for (const ptdvId of [...transition.sourcePTDVIds, transition.targetPTDVId]) {
        const taskSchemaId = this.options.ptdvIdToTaskSchemaId[ptdvId];
        if (taskSchemaId != null && taskSchemaId !== this.rootTaskSchema.id) {
          availableTaskSchemaIdSet.add(taskSchemaId);
        }
      }
    }

    const availableTaskSchemaIds = Array.from(availableTaskSchemaIdSet);

    return {
      rootProcessTaskDefinitionVersion: rootTaskDefinitionVersionSpec,
      allProcessTaskDefinitionVersions: allTaskDefinitionVersionSpecs,
      templateFlowchart: this.getTemplateFlowchartSpec(),
      isFlowchartModifiableOnCreation: this.options.options.isFlowchartModifiableOnCreation,
      isFlowchartModifiableAfterCreation: this.options.options.isFlowchartModifiableAfterCreation,
      availableTaskSchemaIds,
      schemaMapperConfigs: updatedSchemaMapperConfigSpecs,
    };
  }

  private extractTaskDefinitionVersionSpecs(): {
    rootTaskDefinitionVersionSpec: TaskDefinitionVersionSpec;
    allTaskDefinitionVersionSpecs: Array<TaskDefinitionVersionSpec>;
  } {
    const rootNode = this.staticFlowchart.nodesStaticData.find(
      (node) =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) as TaskSchemaStaticNodeData | undefined;
    assert(rootNode != null, 'Expected root node to be defined', {
      nodes: this.staticFlowchart.nodesStaticData,
    });
    const rootTaskDefinitionVersionSpec = convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
      rootNode.taskDefinitionVersionSpec,
      this.options.flowchartConfigId
    );

    // Note, this clobbers duplicate task definition version specs. If we have two nodes using the same
    // task definition version and the same client ID, we will only keep the object for the last node.
    // This may become an issue in the future when making edits to process task definition versions.
    const specsByClientId = new Map(
      this.staticFlowchart.nodesStaticData
        .filter((node) => isStaticTaskSchemaNodeData(node))
        .map((node) => {
          const spec = convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
            node.taskDefinitionVersionSpec,
            this.options.flowchartConfigId
          );
          return [spec.clientId, spec] as const;
        })
    );

    // Optional transitions may reference PTDVs that have no nodes in the flowchart.
    // The backend needs these in allProcessTaskDefinitionVersions to populate its
    // client_id_to_task_definition_version map, which schema mapper configs reference.
    for (const transition of Object.values(this.options.transitions)) {
      if (transition.isPlanned) {
        continue;
      }
      for (const ptdvId of [...transition.sourcePTDVIds, transition.targetPTDVId]) {
        if (!specsByClientId.has(ptdvId)) {
          specsByClientId.set(ptdvId, {
            clientId: ptdvId,
            currentVersionId: ptdvId,
            newProcessTaskDefinitionVersion: null,
          });
        }
      }
    }

    const allTaskDefinitionVersionSpecs = Array.from(specsByClientId.values());
    return {rootTaskDefinitionVersionSpec, allTaskDefinitionVersionSpecs};
  }

  private getNodeDetailsFromStaticNode(
    node: TaskSchemaStaticNodeData | RouterStaticNodeData | EndStaticNodeData
  ): NodeDetailsSpec {
    switch (node.nodeDetailsType) {
      case ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS: {
        assert(isStaticTaskSchemaNodeData(node), 'Expected root node to be a task schema node');
        return {
          type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          taskSchemaId: this.rootTaskSchema.id,
          taskDefinitionVersionClientId: node.taskDefinitionVersionSpec.clientId,
        };
      }
      case ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS: {
        assert(isStaticTaskSchemaNodeData(node), 'Expected output node to be a task schema node');
        assert(
          this.rootTaskSchema.outputSchema != null,
          'Expected flowchart with output node to have output schema',
          {node, flowchartTaskSchema: this.rootTaskSchema}
        );
        return {
          type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
          outputSchemaId: this.rootTaskSchema.outputSchema.id,
        };
      }
      case ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS: {
        assert(isStaticTaskSchemaNodeData(node), 'Expected task node to be a task schema node');
        return {
          type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
          taskSchemaId: node.taskSchemaId,
          taskDefinitionVersionClientId: node.taskDefinitionVersionSpec.clientId,
          extension: null,
        };
      }
      case ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS: {
        assert(isStaticRouterNodeData(node), 'Expected router node to be a router node');
        const routerFunctionSpecs = [
          ...node.routerConfig.functionSpecs,
          node.routerConfig.defaultFunctionSpec,
        ].map((functionSpec) => {
          assert(functionSpec != null, 'Expected router function spec to be defined');
          return convertToRouterConfigSpec(functionSpec, /* flowchartSpecHasEdgeToOutput */ true);
        });
        return {
          type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
          name: node.routerConfig.name,
          routerFunctions: routerFunctionSpecs,
        };
      }
      case ProcessFlowchartNodeDetailsType.END_NODE_DETAILS: {
        assert(isStaticEndNodeData(node), 'Expected end node to be an end node');
        return {
          type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
          name: node.nodeName,
        };
      }
      default:
        assert(false, `Unexpected node ${node.nodeDetailsType}}`);
    }
  }

  private getTemplateFlowchartSpec(): ProcessFlowchartSpec {
    const nodeSpecs: Array<ProcessFlowchartNodeConfigSpec> = [];
    for (const node of this.staticFlowchart.nodesStaticData) {
      assert(
        !isStaticPlaceholderNodeData(node),
        'Expected no placeholder nodes in the static flowchart data when building network spec',
        {node}
      );
      assert(
        !isCollapsedStaticNodeData(node),
        'Expected no collapsed nodes in the static flowchart data when building network spec',
        {node}
      );
      if (this.inboundSchemaMapperConfigsByNodeId[node.id] != null) {
        const inboundSchemaMappers = this.inboundSchemaMapperConfigsByNodeId[node.id];
        const nodeDetails = this.getNodeDetailsFromStaticNode(node);

        const mappableNodeSpec = {
          clientId: node.id,
          nodeDetails,
          inboundSchemaMappers,
        };

        nodeSpecs.push(mappableNodeSpec);
      } else {
        const nodeDetails = this.getNodeDetailsFromStaticNode(node);
        nodeSpecs.push({
          clientId: node.id,
          nodeDetails,
        });
      }
    }

    const edgeSpecs: Array<ProcessFlowchartEdgeConfigSpec> = this.staticFlowchart.edgesStaticData
      .filter((edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW)
      .map((edge) => ({
        clientId: edge.id,
        fromNodeClientId: edge.source,
        toNodeClientId: edge.target,
      }));

    return {
      clientId: this.options.flowchartConfigId,
      nodes: nodeSpecs,
      edges: edgeSpecs,
    };
  }
}

/**
 * Builds a PTDV client ID → task schema ID mapping.
 *
 * Sources:
 * 1. Static nodes in the flowchart
 * 2. Task definitions (covers PTDVs represented only in optional transitions).
 *
 * Useful for passing as `ptdvIdToTaskSchemaId` to {@link buildNetworkFlowchartFromStaticData}.
 */
export function buildPtdvIdToTaskSchemaIdFromNodes(
  nodesStaticData: ReadonlyArray<StaticFlowchartNodeData>,
  taskDefinitions: ReadonlyArray<ConfigurableTaskDefinitionGQL> = []
): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const node of nodesStaticData) {
    if (isStaticTaskSchemaNodeData(node)) {
      mapping[node.taskDefinitionVersionSpec.clientId] = node.taskSchemaId;
    }
  }
  for (const def of taskDefinitions) {
    const ptdvId = def.latestVersion.id;
    if (mapping[ptdvId] == null) {
      mapping[ptdvId] = def.latestVersion.processTaskSchema.id;
    }
  }
  return mapping;
}

export default function buildNetworkFlowchartFromStaticData(
  args: BuildNetworkFlowchartFromStaticDataRunnerArgs
): ProcessFlowchartConfigSpec {
  const runner = new BuildNetworkFlowchartFromStaticDataRunner(args);
  return runner.run();
}
